import { useEffect } from "react";
import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { toast } from "sonner";
import { ContractForm } from "@/features/contracts/ContractForm";
import { useCurrentUser, currentUserQueryOptions } from "@/hooks/useCurrentUser";
import { canCreateContract } from "@/lib/roles";

type Search = { asset_id?: string };

export const Route = createFileRoute("/_authenticated/contracts/new")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    asset_id: typeof s.asset_id === "string" ? s.asset_id : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(currentUserQueryOptions());
    if (!canCreateContract(me.roles)) throw redirect({ to: "/contracts" });
  },
  component: NewContractPage,
});

const EDITOR_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);

function NewContractPage() {
  const { asset_id } = Route.useSearch();
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const canEdit = (me?.roles ?? []).some((r) => EDITOR_ROLES.has(r));

  useEffect(() => {
    if (me && !canEdit) {
      toast.info("Finance users can view contracts but cannot create or edit contracts.");
      navigate({ to: "/contracts", replace: true });
    }
  }, [me, canEdit, navigate]);

  if (!me || !canEdit) return null;
  return <ContractForm initialAssetIds={asset_id ? [asset_id] : []} />;
}
